import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../services/firebase";
import menus from "../../app/navigation";
import logo from "../../assets/images/visuals/logo_apocalypse_lovers.png";
import { useAuth } from "../../features/auth/AuthProvider"; // Custom hook for auth context
import icon_club_plus from "../../assets/images/subscription/icon_club_plus.webp";
import counter from "../../assets/images/subscription/counter.webp";
import { IoIosArrowDown } from "react-icons/io";
import useGoogleTranslate from "../../hooks/useGoogleTranslate";
import GoogleTranslateSelect, {
  GOOGLE_TRANSLATE_ELEMENT_ID,
} from "./GoogleTranslateSelect";
import UserPopup from "../userPopup/UserPopup";
import "./styles.scss";

const Header = () => {
  const menuRef = useRef(null);
  const menuToggleRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [scroll, setScroll] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveredUser, setIsHoveredUser] = useState(false);
  const { isReady: isGoogleTranslateReady } = useGoogleTranslate({
    elementId: GOOGLE_TRANSLATE_ELEMENT_ID,
    pageLanguage: "en",
  });
  const { user } = useAuth();
  const firebaseUser = user?.firebaseUser;
  const hoverTimeout = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScroll(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !menuToggleRef.current?.contains(event.target)
      ) {
        setMenuActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    setMenuActive(false);
    setActiveIndex(null);
  }, [location.pathname, location.hash]);

  const handleMouseEnterUser = () => {
    clearTimeout(hoverTimeout.current);
    setIsHoveredUser(true);
  };

  const handleMouseLeaveUser = () => {
    hoverTimeout.current = setTimeout(() => {
      setIsHoveredUser(false);
    }, 200);
  };

  const toggleMenu = () => {
    setMenuActive((prev) => !prev);
  };

  const handleDropdown = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const handleLogout = async () => {
    if (!window.confirm("Are you sure that you want to logout?")) {
      return;
    }

    try {
      await signOut(auth);
      navigate("/log-in");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <header
      id="header_main"
      className={`header ${scroll ? "is-fixed" : ""} ${
        user ? "is-logged-in" : "is-logged-out"
      }`}
    >
      <div className="container big">
        <div className="row">
          <div className="col-12">
            <div className="header__body">
              <div className="header__action__phone">
                {user ? (
                  <div
                    className="icon-wrapper-phone"
                    onMouseEnter={handleMouseEnterUser}
                    onMouseLeave={handleMouseLeaveUser}
                  >
                    <div className="biohazard-icon">
                      <img src={counter} alt="USER" className="w-1 h-1" />
                    </div>
                    <div className="arrow">
                      <IoIosArrowDown />
                    </div>

                    {isHoveredUser && (
                      <UserPopup
                        displayName={
                          firebaseUser?.displayName || firebaseUser?.email
                        }
                      />
                    )}
                  </div>
                ) : (
                  <Link to="/log-in" className="action-btn">
                    <span>Join Now</span>
                  </Link>
                )}
              </div>

              <div className="header__logo">
                <Link to="/">
                  <img
                    className="header__img__logo"
                    id="site-logo"
                    src={logo}
                    alt="apocalypse lovers"
                  />
                </Link>
              </div>

              <div className="header__right">
                <nav
                  id="main-nav"
                  className={`main-nav ${menuActive ? "active" : "is-closed"}`}
                  ref={menuRef}
                >
                  {user && (
                    <div className="mobile-user-info">
                      <span className="px-3 py-4">
                        {firebaseUser?.displayName || firebaseUser?.email}
                      </span>
                      <hr className="my-2 border-light opacity-25" />
                    </div>
                  )}

                  <div className="mobile-language-selector">
                    <GoogleTranslateSelect
                      disabled={!isGoogleTranslateReady}
                    />
                  </div>

                  <ul id="menu-primary-menu" className="menu">
                    {menus.map((data, idx) => (
                      <li
                        key={idx}
                        onClick={(e) => {
                          if (!data.namesub) {
                            e.stopPropagation();
                          } else {
                            handleDropdown(idx);
                          }
                        }}
                        className={`menu-item ${
                          data.namesub ? "menu-item-has-children" : ""
                        } ${activeIndex === idx ? "active" : ""}`}
                      >
                        <Link to={data.links}>{data.name}</Link>
                        {data.namesub && (
                          <ul
                            className="sub-menu"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {data.namesub.map((submenu) => (
                              <li key={submenu.id} className="menu-item">
                                <NavLink to={submenu.links}>
                                  {submenu.sub}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                    {user?.isAdmin && (
                      <li className="menu-item">
                        <Link to="/admin">Admin</Link>
                      </li>
                    )}
                  </ul>
                  {user && (
                    <div
                      className="mobile-user-logout p-3 align-items-center justify-content-end"
                      style={{ marginTop: "50px" }}
                    >
                      <span
                        onClick={handleLogout}
                        className=" p-0 text-danger"
                        title="Log out of your account"
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        style={{
                          cursor: "pointer",
                          textDecoration: isHovered ? "underline" : "none",
                        }}
                      >
                        Log Out
                      </span>
                    </div>
                  )}
                </nav>

                <div
                  id="menu-toggle-btn"
                  className={`mobile-button ${menuActive ? "active" : ""}`}
                  onClick={toggleMenu}
                  ref={menuToggleRef}
                >
                  <span></span>
                </div>
              </div>

              <div className="header__action">
                <GoogleTranslateSelect
                  disabled={!isGoogleTranslateReady}
                  renderWidget
                />

                <Link to="/subscription?tab=buyessence">
                  <div className="club-button">
                    <img
                      src={icon_club_plus}
                      alt="Club Plus"
                      className="club-icon"
                    />
                    <span>Club+</span>
                  </div>
                </Link>

                {user ? (
                  <div
                    className="icon-wrapper"
                    onMouseEnter={handleMouseEnterUser}
                    onMouseLeave={handleMouseLeaveUser}
                  >
                    <div className="biohazard-icon">
                      <img src={counter} alt="USER" className="w-1 h-1" />
                    </div>
                    <div className="arrow">
                      <IoIosArrowDown />
                    </div>

                    {isHoveredUser && (
                      <UserPopup
                        displayName={
                          firebaseUser?.displayName || firebaseUser?.email
                        }
                      />
                    )}
                  </div>
                ) : (
                  <Link to="/log-in">
                    <div className="join-now-header-btn">
                      <span>Join Now</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
